import { useCallback, useState } from 'react'
import { suspend } from 'suspend-react'
import { setAuthToken } from '../api/client'
import { ayeApi } from '../core'
import { DeleteAccountMethodRequestResponse } from '../core/api/http-methods/delete-account'
import { DeletePaymentMethodRequestResponse } from '../core/api/http-methods/delete-payment-method'
import { GetOtpResponse } from '../core/api/http-methods/get-otp'
import { GetPaymentMethodsResponse, PaymentConsents } from '../core/api/http-methods/get-payment-methods'
import { GetUserTransactionsResponse } from '../core/api/http-methods/get-user-transactions'
import { LoginPortalResponse } from '../core/api/http-methods/login'
import { PayWithoutPINResponse } from '../core/api/http-methods/pay-without-pin'
import { ResetPINResponse } from '../core/api/http-methods/reset-pin'
import { SaveUserResponse } from '../core/api/http-methods/save-user-info'
import { SetDefaultResponse } from '../core/api/http-methods/set-default'
import { UserI } from '../types/user'
import catchAsync from '../utils/catchAsync'
import useGuardedCallback, { GuardedReturn } from '../utils/useGuardedCallback'

export function usePortalHook(): {
  token: string | undefined
  user: UserI | undefined
  loading: boolean
  getCode: (
    phoneNumber: string,
    eventType: string,
  ) => Promise<GuardedReturn<GetOtpResponse>>
  login: (
    phoneNumber: string,
    code: string,
  ) => Promise<GuardedReturn<LoginPortalResponse>>
  logout: () => void
  saveUser: (args_0: {
    user_name: string
    user_email: string
    user_birthday: string
    newsletter: boolean
    marketing_and_offers: boolean
    text_message: boolean
  }) => Promise<GuardedReturn<SaveUserResponse | undefined>>
  // addPaymentMethod: (
  //   args_0: AddPaymentMethodRequest['body'],
  // ) => Promise<GuardedReturn<AddPaymentMethodResponse | undefined>>
  deletePaymentMethod: (args_0: {
    phoneNumber: string
    consentId: string
  }) => Promise<GuardedReturn<DeletePaymentMethodRequestResponse | undefined>>
  transactions: (
    phoneNumber: string,
  ) => Promise<GetUserTransactionsResponse | undefined>
  paymentMethods: () => Promise<
    GetPaymentMethodsResponse['payment_consents'] | undefined
  >
  // verifyConsent: (
  //   token: string,
  // ) => Promise<GuardedReturn<VerifyConsentResponse | undefined>>
  setDefault: (
    consentId: string,
  ) => Promise<GuardedReturn<SetDefaultResponse | undefined>>
  // updatePIN: (
  //   oldPin: string,
  //   newPin: string,
  //   transactionLimit: number,
  // ) => Promise<GuardedReturn<UpdatePINResponse | undefined>>
  // updateFace: (
  //   face_index: string,
  // ) => Promise<GuardedReturn<UpdateFaceResponse | undefined>>
  resetPIN: (
    newPin: string,
    otp: string,
    phoneNumber: string,
  ) => Promise<GuardedReturn<ResetPINResponse | undefined>>
  deleteAccount: (
    phoneNumber: string,
    otp: string,
  ) => Promise<GuardedReturn<DeleteAccountMethodRequestResponse | undefined>>
  payWithOutPin: (
    scanAndPayWithOutPin: boolean,
    transactionLimit: number,
    phoneNumber: string,
    otp: string | number,
  ) => Promise<GuardedReturn<PayWithoutPINResponse | undefined>>
} {
  const [counter, setCounter] = useState<number>(0)
  const [token, setToken] = useState<string>()
  const [loading, setLoading] = useState<boolean>(false)
  const user = suspend(async () => {
    const localStorageToken = localStorage.getItem('token')
    if (localStorageToken) {
      const headers = new Headers()
      headers.append('Authorization', `Bearer ${localStorageToken}`)
      setToken(localStorageToken)
      try {
        const userResponse = await ayeApi.getUser({ headers })
        return userResponse?.success === true ? userResponse.data : undefined
      } catch (error) {
        // localStorage.removeItem('token');
        // setToken(undefined);
      }
    }
  }, [counter])

  const getCode = useGuardedCallback(
    async (phoneNumber: string, eventType: string) => {
      const [country_code, user_phone_number] = phoneNumber.split('-')
      const response = await ayeApi.getOTP({
        body: {
          phoneNumber: user_phone_number,
          countryCode: country_code.startsWith('+')
            ? country_code
            : `+${country_code}`,
          eventType,
        },
      })
      return response
    },
    [],
    setLoading,
  )

  // const updateFace = useGuardedCallback(async (face_index: string) => {
  //   const response = await ayeApi.updateFace({
  //     body: {
  //       face_index,
  //     },
  //   })
  //   return response
  // })

  const login = useGuardedCallback(
    async (phoneNumber: string, code: string) => {
      const [country_code, user_phone_number] = phoneNumber.split('-')
      const response = await ayeApi.loginPortal({
        body: {
          phoneNumber: user_phone_number,
          countryCode: country_code.startsWith('+')
            ? country_code
            : `+${country_code}`,
          otp: code,
        },
      })
      localStorage.setItem('LoggedIn', 'true')
      if (response.success && response.data?.accessToken) {
        console.debug(
          'ℹ️ ~ file: usePortalHook.tsx:132 ~ response.auth_token:',
          response.data.accessToken,
        )
        localStorage.setItem('token', response.data.accessToken)
        setAuthToken(response.data.accessToken)

        setCounter((prev) => prev + 1)
      }
      return response
    },
    [],
    setLoading,
  )
  const logout = () => {
    console.log('Logging out...')
    localStorage.removeItem('token')
    setToken(undefined)
    setCounter((prev) => prev + 1)
  }

  const saveUser = useGuardedCallback(
    async ({
      user_name,
      user_email,
      user_birthday,
      text_message,
      newsletter,
      marketing_and_offers,
    }: {
      user_name: string
      user_email: string
      user_birthday: string
      marketing_and_offers: boolean
      newsletter: boolean
      text_message: boolean
    }) => {
      const localStorageToken = localStorage.getItem('token')
      if (localStorageToken) {
        const headers = new Headers()
        headers.append('Authorization', `Bearer ${localStorageToken}`)
        const response = await ayeApi.saveUser({
          body: {
            name: user_name,
            email: user_email,
            birthday: new Date(user_birthday).toISOString(),
            textMessage: text_message,
            newsletter,
            marketingAndOffers: marketing_and_offers,
          },
          headers,
        })
        setCounter((prev) => prev + 1)
        return response
      }
    },
    [token],
  )
  const deletePaymentMethod = useGuardedCallback(
    async ({
      phoneNumber,
      consentId,
    }: {
      phoneNumber: string
      consentId: string
    }) => {
      const localStorageToken = localStorage.getItem('token')
      if (localStorageToken) {
        const headers = new Headers()
        headers.append('Authorization', `Bearer ${localStorageToken}`)

        const response = await ayeApi.deletePaymentMethod({
          body: { consentId },
          headers,
        })
        // setCounter(prev=> prev+1)
        return response
      }
    },
    [token],
  )
  // const addPaymentMethod = useGuardedCallback(
  //   async (args: AddPaymentMethodRequest['body']) => {
  //     const localStorageToken = localStorage.getItem('token')
  //     if (localStorageToken) {
  //       const headers = new Headers()
  //       headers.append('Authorization', `Bearer ${localStorageToken}`)
  //       const response = await ayeApi.addPaymentMethod({ body: args, headers })
  //       setCounter((prev) => prev + 1)
  //       return response
  //     }
  //   },
  //   [],
  //   setLoading,
  // )
  const paymentMethods = useCallback(async () => {
    const localStorageToken = localStorage.getItem('token')

    if (localStorageToken) {
      const headers = new Headers()
      headers.append('Authorization', `Bearer ${localStorageToken}`)
      const response = await catchAsync(
        async () => await ayeApi.getPaymentMethods({ headers }),
      )()
      if (response.isOk()) return response.response?.payment_consents
      return []
    }

    return []
  }, [token])

  const transactions = useCallback(
    async (phoneNumber: string) => {
      const localStorageToken = localStorage.getItem('token')
      if (localStorageToken) {
        const headers = new Headers()
        headers.append('Authorization', `Bearer ${localStorageToken}`)
        const response = await ayeApi.getUserTransactions({
          headers,
        })
        return response
      }
    },
    [token],
  )
  // const verifyConsent = useGuardedCallback(async (token: string) => {
  //   const headers = new Headers()
  //   console.log('====================================')
  //   console.log({ token })
  //   console.log('====================================')
  //   // headers.append('Authorization', `Bearer ${token}`);
  //   const response = await ayeApi.verifyConsent({
  //     body: {
  //       token: token,
  //     },
  //     headers,
  //   })
  //   return response
  // }, [])

  const setDefault = useGuardedCallback(async (consentId: string) => {
    const headers = new Headers()
    console.log('====================================')
    console.log({ consentId })
    console.log('====================================')
    const localStorageToken = localStorage.getItem('token')
    if (localStorageToken) {
      headers.append('Authorization', `Bearer ${localStorageToken}`)
    }
    const response = await ayeApi.setDefault({
      body: {
        consentId: consentId,
      },
      headers,
    })
    return response
  }, [])
  // const updatePIN = useGuardedCallback(
  //   async (oldPin: string, newPin: string, transactionLimit: number) => {
  //     const headers = new Headers()
  //     console.log('====================================')
  //     console.log({ newPin })
  //     console.log('====================================')
  //     const localStorageToken = localStorage.getItem('token')
  //     if (localStorageToken) {
  //       headers.append('Authorization', `Bearer ${localStorageToken}`)
  //     }

  //     const response = await ayeApi.UpdatePIN({
  //       body: {
  //         oldPin,
  //         newPin,
  //         transactionLimit,
  //       },
  //       headers,
  //     })
  //     return response
  //   },
  //   [],
  // )
  const resetPIN = useGuardedCallback(
    async (newPin: string, otp: string, phoneNumber: string) => {
      const headers = new Headers()
      console.log('====================================')
      console.log({ newPin })
      console.log('====================================')
      const localStorageToken = localStorage.getItem('token')
      if (localStorageToken) {
        headers.append('Authorization', `Bearer ${localStorageToken}`)
      }

      const response = await ayeApi.ResetPIN({
        body: {
          newPin,
          otp,
        },
        headers,
      })
      return response
    },
    [],
  )
  const deleteAccount = useGuardedCallback(
    async (phoneNumber: string, otp: string) => {
      const headers = new Headers()
      const localStorageToken = localStorage.getItem('token')
      if (localStorageToken) {
        headers.append('Authorization', `Bearer ${localStorageToken}`)
      }

      const response = await ayeApi.deleteAccount({
        body: {
          otp,
        },
        headers,
      })
      return response
    },
    [],
  )
  const payWithOutPin = useGuardedCallback(
    async (
      scanAndPayWithOutPin: boolean,
      transactionLimit: number,
      phoneNumber: string,
      pin: string | number,
    ) => {
      const headers = new Headers()
      const localStorageToken = localStorage.getItem('token')
      if (localStorageToken) {
        headers.append('Authorization', `Bearer ${localStorageToken}`)
      }

      const response = await ayeApi.PayWithoutPIN({
        body: {
          scanAndPayWithOutPin,
          transactionLimit,
          pin,
        },
        headers,
      })
      return response
    },
    [],
  )

  return {
    token,
    user,
    loading,
    getCode,
    login,
    logout,
    saveUser,
    // addPaymentMethod,
    deletePaymentMethod,
    paymentMethods,
    transactions,
    // verifyConsent,
    setDefault,
    // updatePIN,
    resetPIN,
    deleteAccount,
    payWithOutPin,
    // updateFace,
  }
}
